import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../contexts/CurrentUserContext";
import { getSketchDesignProjectsWithApprovalsByUser } from "../../../api/projectApi";
import {
  getSketchDesignApproval,
  updateSketchDesignApproverStatus,
} from "../../../api/projectEstimatesApi";
import Preloader from "../../Preloader/Preloader";
import "./SketchDesignApproval.css";

function SketchDesignApproval() {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const BATCH_SIZE = 8;

    const fetchItems = async () => {
      if (!userData?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const projects = await getSketchDesignProjectsWithApprovalsByUser();
        if (cancelled) return;

        const list = [];
        for (let i = 0; i < projects.length; i += BATCH_SIZE) {
          const batch = projects.slice(i, i + BATCH_SIZE);
          const batchCalls = batch.flatMap((p) => [
            getSketchDesignApproval(p.id, "commercial_offer").then((a) => ({
              project: p,
              chapter: "commercial_offer",
              approval: a,
            })),
            getSketchDesignApproval(p.id, "estimate").then((a) => ({
              project: p,
              chapter: "estimate",
              approval: a,
            })),
          ]);
          const results = await Promise.all(batchCalls);
          if (cancelled) return;
          for (const { project, chapter, approval } of results) {
            if (!approval?.approvers?.length) continue;
            const myApprover = approval.approvers.find(
              (a) =>
                a.user?.id === userData.id ||
                String(a.user_id) === String(userData.id)
            );
            if (!myApprover) continue;
            list.push({
              id: `${project.id}-${chapter}`,
              projectId: project.id,
              projectName: project.name || approval.project?.name || "—",
              chapter,
              approval,
              myApprover,
            });
          }
        }
        if (!cancelled) setItems(list);
      } catch (err) {
        if (!cancelled) {
          console.error("Ошибка загрузки согласований ЭП:", err);
          setError("Ошибка при загрузке списка согласований");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const getStatusIcon = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован")
      return <span className="sd-approval__status-icon approved">✓</span>;
    if (s === "in_processing" || s === "pending" || s === "на рассмотрении")
      return <span className="sd-approval__status-icon pending">⏳</span>;
    if (s === "rejected" || s === "отклонен")
      return <span className="sd-approval__status-icon rejected">✕</span>;
    return <span className="sd-approval__status-icon pending">⏳</span>;
  };

  const getStatusText = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") return "Согласован";
    if (s === "rejected" || s === "отклонен") return "Отклонён";
    return "На рассмотрении";
  };

  const handleSetStatus = async (item, status) => {
    if (!item.myApprover?.id) return;
    setUpdatingId(item.id);
    try {
      await updateSketchDesignApproverStatus(item.myApprover.id, status);
      const updated = await getSketchDesignApproval(
        item.projectId,
        item.chapter
      );
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                approval: updated,
                myApprover:
                  updated?.approvers?.find(
                    (a) =>
                      a.user?.id === userData?.id ||
                      String(a.user_id) === String(userData?.id)
                  ) || i.myApprover,
              }
            : i
        )
      );
    } catch (err) {
      console.error("Ошибка обновления статуса:", err);
      alert(err?.message || "Ошибка при обновлении статуса");
    } finally {
      setUpdatingId(null);
    }
  };

  const openDocument = (item) => {
    const tab = item.chapter === "commercial_offer" ? "offer" : "estimate";
    navigate(`/projects/sketch-design/finance/${item.projectId}?tab=${tab}`);
  };

  const chapterLabel = (chapter) =>
    chapter === "commercial_offer" ? "Коммерческое предложение" : "Смета";

  if (loading) return <Preloader />;

  if (error) {
    return (
      <div className="sd-approval">
        <div className="sd-approval__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="sd-approval">
      <h2 className="sd-approval__title">Согласование проектов ЭП</h2>
      <p className="sd-approval__subtitle">
        Здесь отображаются разделы (КП и смета) проектов эскизного
        проектирования, в которых вы указаны согласующим.
      </p>

      {items.length === 0 ? (
        <div className="sd-approval__empty">
          Нет документов на ваше согласование
        </div>
      ) : (
        <div className="sd-approval__table-container">
          <table className="sd-approval__table">
            <thead>
              <tr>
                <th>Проект</th>
                <th>Раздел</th>
                <th>Мой статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const status = item.myApprover?.status;
                const isPending =
                  !status ||
                  status === "in_processing" ||
                  String(status).toLowerCase() === "pending" ||
                  String(status).toLowerCase() === "на рассмотрении";
                const isUpdating = updatingId === item.id;

                return (
                  <tr key={item.id} className="sd-approval__row">
                    <td>
                      <button
                        type="button"
                        className="sd-approval__project-link"
                        onClick={() => openDocument(item)}
                      >
                        {item.projectName}
                      </button>
                    </td>
                    <td>{chapterLabel(item.chapter)}</td>
                    <td>
                      <div className="sd-approval__status-cell">
                        {getStatusIcon(status)}
                        <span>{getStatusText(status)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="sd-approval__actions">
                        <button
                          type="button"
                          className="sd-approval__btn-open"
                          onClick={() => openDocument(item)}
                        >
                          Открыть документ
                        </button>
                        {isPending && (
                          <>
                            <button
                              type="button"
                              className="sd-approval__btn-approve"
                              onClick={() =>
                                handleSetStatus(item, "approved")
                              }
                              disabled={isUpdating}
                            >
                              {isUpdating ? "..." : "Согласовать"}
                            </button>
                            <button
                              type="button"
                              className="sd-approval__btn-reject"
                              onClick={() =>
                                handleSetStatus(item, "rejected")
                              }
                              disabled={isUpdating}
                            >
                              Отклонить
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SketchDesignApproval;
